import type { Event } from "@/apiTypes/event";
import { palette } from "@/styles/colors";

type EventWithRelations = Event & {
  imageUrl?: string | null;
  eventTypeLabel?: string | null;
};

/**
 * Sums participant counts across all categories on an event.
 *
 * @param event - The event to evaluate
 * @returns Total participant count, or null if no data available
 */
export function getTotalParticipants(event: EventWithRelations): number | null {
  const counts =
    event.categories
      ?.map((cat) => cat.participantCount)
      .filter((c): c is number => c !== null && c !== undefined) ?? [];
  if (counts.length === 0) return null;
  return counts.reduce((a, b) => a + b, 0);
}

/**
 * Extracts distance, elevation, and GPX download URL from the first
 * relevant route attached to the event.
 *
 * @param event - The event to evaluate
 * @returns An object with distance (meters), elevation (meters), and gpxUrl
 */
export function getRouteStats(event: {
  routes?: {
    distance_meters: number | null;
    elevation_gain_meters: number | null;
    routeFileUrl?: string | null;
  }[];
}): {
  distance: number | null;
  elevation: number | null;
  gpxUrl: string | null;
} {
  const routes = event.routes ?? [];
  if (routes.length === 0)
    return { distance: null, elevation: null, gpxUrl: null };

  const routeWithData = routes.find(
    (r) => r.distance_meters || r.elevation_gain_meters,
  );
  const routeWithGpx = routes.find((r) => r.routeFileUrl);

  return {
    distance: routeWithData?.distance_meters ?? null,
    elevation: routeWithData?.elevation_gain_meters ?? null,
    gpxUrl: routeWithGpx?.routeFileUrl ?? null,
  };
}

/**
 * DEPRECATED: Returns the display location string for an event.
 *
 * @param event - The event to evaluate
 * @returns Location string, or null if unavailable
 */
export function getLocationString(event: {
  location: string | null;
}): string | null {
  if (event.location) return event.location;
  return null;
}

/**
 * Maps an externalId prefix to a human-readable registration platform name.
 *
 * @param externalId - The event's externalId
 * @returns Platform name or null for native events
 */
export function getRegistrationPlatform(
  externalId: string | null,
): string | null {
  if (!externalId) return null;
  if (externalId.startsWith("bikereg:")) return "BikeReg";
  if (externalId.startsWith("active:")) return "Active";
  if (externalId.startsWith("rusa:")) return "RUSA";
  return null;
}

/**
 * Resolves source attribution (friendly name + URL) for linking back
 * to the original event listing.
 *
 * @param sourceUrl - The raw source URL from the event
 * @returns Object with `name` and `url`, or null if no source
 */
export function getSourceAttribution(
  sourceUrl: string | null | undefined,
): { name: string; url: string } | null {
  if (!sourceUrl) return null;

  try {
    const url = new URL(sourceUrl);
    const hostname = url.hostname.toLowerCase();

    const knownSources: Record<string, string> = {
      "bikereg.com": "BikeReg",
      "www.bikereg.com": "BikeReg",
      "usacycling.org": "USA Cycling",
      "www.usacycling.org": "USA Cycling",
      "ridewithgps.com": "Ride with GPS",
      "www.ridewithgps.com": "Ride with GPS",
      "strava.com": "Strava",
      "www.strava.com": "Strava",
      "eventbrite.com": "Eventbrite",
      "www.eventbrite.com": "Eventbrite",
      "active.com": "Active",
      "www.active.com": "Active",
      "runsignup.com": "RunSignUp",
      "www.runsignup.com": "RunSignUp",
    };

    const friendlyName =
      knownSources[hostname] ||
      hostname
        .replace(/^www\./, "")
        .split(".")[0]
        .charAt(0)
        .toUpperCase() +
        hostname
          .replace(/^www\./, "")
          .split(".")[0]
          .slice(1);

    return { name: friendlyName, url: sourceUrl };
  } catch {
    return null;
  }
}

/**
 * Maps an event type string to a Tailwind background colour class
 * for use in badge/pill UI elements.
 *
 * @param eventType - The raw event type value
 * @param useTailwind - @default true Whether to use Tailwind vars or raw HEX
 * @returns Tailwind background class name
 */
export function getEventTypeBadgeColor(
  eventType: string,
  useTailwind = true,
): string {
  switch (eventType) {
    case "race":
      return useTailwind ? "bg-primary" : palette.primary[500];
    case "ride":
      return useTailwind ? "bg-secondary" : palette.secondary[500];
    case "route":
      return useTailwind ? "bg-emerald-600" : palette.success[500];
    default:
      return useTailwind ? "bg-gray-500" : palette.neutral[500];
  }
}
